import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { Bar } from "react-chartjs-2";
import { alpha } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { Fragment, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import RefreshIcon from "@mui/icons-material/Refresh";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";
import { usePrefs } from "../../../services/prefs";
import { useApiTenant } from "../../../api/api-tenant";
export default function ServiceEfficiencyMetrics() {
    const { t } = useTranslation();
    const prefs = usePrefs();
    const api = useApiTenant();
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [cachedAt, setCachedAt] = useState(new Date());
    const [interval, setInterval] = useState("this_month");
    const [definitions, setDefinitions] = useState([]);
    const isMobile = useMediaQuery("(max-width: 767px)");
    const fetchData = (invalidateCache) => {
        setIsLoading(true);
        if (isError) {
            setIsError(false);
        }
        api.GET_serviceEfficiencyMetrics(interval, invalidateCache ? 1 : undefined)
            .then(response => {
            setDefinitions(response.data.data);
            setCachedAt(new Date(response.data.cached_at));
            setIsLoading(false);
        })
            .catch(error => {
            setIsError(true);
            if (!api.isCancel(error)) {
                console.error(error);
                setIsLoading(false);
            }
        });
        return () => api.cancel();
    };
    useEffect(() => {
        fetchData(undefined);
    }, [interval]);
    if (isMobile) {
        return null;
    }
    // Don't render the widget at all if there are no definitions configured
    if (!isLoading && !isError && definitions.length === 0) {
        return null;
    }
    const intervalOptions = [
        { value: "day", label: t("metrics.range-options.today") },
        { value: "yesterday", label: t("metrics.range-options.yesterday") },
        { value: "this_week", label: t("metrics.range-options.this-week") },
        { value: "this_month", label: t("metrics.range-options.this-month") },
        { value: "last_month", label: t("metrics.range-options.last-month") },
        { value: "this_year", label: t("metrics.range-options.this-year") },
    ];
    return (_jsx(Box, Object.assign({ sx: { marginBottom: 4 }, "data-testid": "service-efficiency-metrics" }, { children: _jsx(Card, { children: _jsxs(Box, Object.assign({ sx: { padding: 3 } }, { children: [_jsxs(Box, Object.assign({ sx: {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 2,
                        } }, { children: [_jsx(Typography, Object.assign({ variant: "button" }, { children: t("metrics.service-efficiency") })), _jsx(Select, Object.assign({ variant: "standard", value: interval, onChange: e => {
                                    setInterval(e.target.value);
                                    setIsLoading(true);
                                } }, { children: intervalOptions.map(opt => (_jsx(MenuItem, Object.assign({ value: opt.value }, { children: opt.label }), opt.value))) }))] })), isLoading ? (_jsx(Box, Object.assign({ sx: {
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "400px",
                        } }, { children: _jsx(CircularProgress, { color: "primary", size: 30 }) }))) : isError ? (_jsx(Box, Object.assign({ sx: {
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "400px",
                        } }, { children: _jsx(Typography, Object.assign({ variant: "caption", color: "textSecondary" }, { children: t("metrics.error-occured") })) }))) : (_jsxs(Fragment, { children: [_jsx(Grid, Object.assign({ container: true, spacing: 4 }, { children: definitions.map(def => (_jsxs(Grid, Object.assign({ item: true, xs: 12, md: 6 }, { children: [_jsx(Typography, Object.assign({ variant: "subtitle2", sx: { marginBottom: 1 } }, { children: def.name })), _jsxs(Box, Object.assign({ sx: {
                                                display: "flex",
                                                gap: 3,
                                                marginBottom: 1,
                                            } }, { children: [_jsxs(Box, { children: [_jsx(Typography, Object.assign({ variant: "caption", color: "textSecondary" }, { children: t("metrics.efficiency-line-label") })), _jsx(Typography, Object.assign({ variant: "h6" }, { children: def.average_response_time_minutes != null
                                                                ? formatMinutes(def.average_response_time_minutes)
                                                                : "-" }))] }), _jsxs(Box, { children: [_jsx(Typography, Object.assign({ variant: "caption", color: "textSecondary" }, { children: t("metrics.efficiency-bar-primary-label") })), _jsx(Typography, Object.assign({ variant: "h6" }, { children: def.ticket_count }))] })] })), def.trend.length > 0 && (_jsx(Box, Object.assign({ sx: { height: "250px" } }, { children: _jsx(Bar, { data: {
                                                    labels: def.trend.map(p => p.date),
                                                    datasets: [
                                                        {
                                                            label: t("metrics.efficiency-line-label"),
                                                            data: def.trend.map(p => p.average_response_time_minutes),
                                                            // @ts-ignore
                                                            type: "line",
                                                            yAxisID: "avgTime",
                                                            backgroundColor: prefs.theme === "light"
                                                                ? "#000"
                                                                : "#fff",
                                                            borderColor: alpha(prefs.theme === "light"
                                                                ? "#000"
                                                                : "#fff", 0.6),
                                                            borderWidth: 2,
                                                            pointRadius: 3,
                                                        },
                                                        {
                                                            label: t("metrics.efficiency-bar-primary-label"),
                                                            data: def.trend.map(p => p.ticket_count),
                                                            yAxisID: "count",
                                                            backgroundColor: alpha("#fbbc0e", 0.3),
                                                            borderColor: "#fbbc0e",
                                                            borderWidth: 1,
                                                        },
                                                        {
                                                            label: t("metrics.efficiency-bar-secondary-label"),
                                                            data: def.trend.map(p => p.open_ticket_count),
                                                            yAxisID: "count",
                                                            backgroundColor: alpha("#165331", 0.3),
                                                            borderColor: "#165331",
                                                            borderWidth: 1,
                                                        },
                                                    ],
                                                }, options: {
                                                    maintainAspectRatio: false,
                                                    animation: { duration: 0 },
                                                    plugins: {
                                                        legend: {
                                                            position: "bottom",
                                                            labels: {
                                                                boxWidth: 3,
                                                                boxHeight: 3,
                                                                usePointStyle: true,
                                                                pointStyle: "circle",
                                                                color: prefs.theme === "light"
                                                                    ? "rgba(0, 0, 0, 0.87)"
                                                                    : "#fff",
                                                            },
                                                        },
                                                        tooltip: {
                                                            callbacks: {
                                                                label: function (context) {
                                                                    if (
                                                                    // @ts-ignore
                                                                    context.dataset.type ===
                                                                        "line") {
                                                                        return (context.dataset
                                                                            .label +
                                                                            ": " +
                                                                            formatMinutes(context.parsed.y));
                                                                    }
                                                                    return (context.dataset.label +
                                                                        ": " +
                                                                        context.parsed.y);
                                                                },
                                                            },
                                                        },
                                                    },
                                                    scales: {
                                                        x: {
                                                            ticks: {
                                                                color: prefs.theme === "light"
                                                                    ? "rgba(0, 0, 0, 0.6)"
                                                                    : "rgba(255, 255, 255, 0.7)",
                                                            },
                                                        },
                                                        avgTime: {
                                                            type: "linear",
                                                            display: true,
                                                            position: "left",
                                                            beginAtZero: true,
                                                            ticks: {
                                                                color: prefs.theme === "light"
                                                                    ? "rgba(0, 0, 0, 0.6)"
                                                                    : "rgba(255, 255, 255, 0.7)",
                                                                callback: function (value) {
                                                                    return formatAxisLabel(value);
                                                                },
                                                            },
                                                        },
                                                        count: {
                                                            type: "linear",
                                                            display: true,
                                                            position: "right",
                                                            beginAtZero: true,
                                                            grid: {
                                                                drawOnChartArea: false,
                                                            },
                                                            ticks: {
                                                                color: prefs.theme === "light"
                                                                    ? "rgba(0, 0, 0, 0.6)"
                                                                    : "rgba(255, 255, 255, 0.7)",
                                                            },
                                                        },
                                                    },
                                                } }) })))] }), def.definition_id))) })), _jsx(Box, Object.assign({ sx: { marginTop: 2 } }, { children: _jsxs(Box, Object.assign({ sx: {
                                        display: "flex",
                                        justifyContent: "flex-start",
                                        alignItems: "center",
                                    } }, { children: [_jsxs(Typography, Object.assign({ variant: "caption", color: "textSecondary" }, { children: [t("metrics.cached-at"), cachedAt.toLocaleDateString("en-GB") +
                                                    " " +
                                                    cachedAt.toLocaleTimeString("hr-HR", {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })] })), _jsxs(Button, Object.assign({ size: "small", onClick: () => fetchData(true), style: {
                                                padding: 0,
                                                marginLeft: "5px",
                                                color: prefs.theme === "light" ? "#00000099" : "#ffffffb3",
                                                fontSize: "11px",
                                                textTransform: "none",
                                            } }, { children: [_jsx(RefreshIcon, { fontSize: "small", style: { marginRight: 2 }, color: "action" }), t("metrics.refresh-data")] }))] })) }))] }))] })) }) })));
}
function formatMinutes(minutes) {
    if (minutes <= 0) {
        return "0 min";
    }
    if (minutes < 60) {
        return `${Math.round(minutes)} min`;
    }
    const totalMins = Math.round(minutes);
    const hours = Math.floor(totalMins / 60);
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    const mins = totalMins % 60;
    if (days > 0) {
        const parts = [`${days}d`];
        if (remainingHours > 0)
            parts.push(`${remainingHours}h`);
        if (mins > 0)
            parts.push(`${mins}m`);
        return parts.join(" ");
    }
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}
function formatAxisLabel(minutes) {
    if (minutes <= 0) {
        return "0";
    }
    const days = Math.round(minutes / 60 / 24);
    return `${days}d`;
}
